import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { SplaAgreementType, SplaCategory, SplaLicense, SplaLicensePricing } from '../types/models/Spla';
import type {
  ApiResponseSplaCategoryList,
  ApiResponseSplaLicenseList,
  ApiResponseSplaLicensePricing,
} from '../types/api/ApiResponseSpla';

// Staff-only SPLA catalog hooks (GET /admin/spla/licenses, /admin/spla/categories and the
// per-license sale-price grid). All follow the useAdminProducts(enabled) deferred-fetch shape: the
// page passes its `is_ancalagon_staff` check in, so a non-staff visitor (shown <NotFound />) never
// fires a backend-rejected admin call. Every consumer branches on the JSend `body.status`, NEVER
// `response.ok` — the backend maps controller exceptions to `status: 'error'` at HTTP 200.

export interface AdminSplaLicenseFilters {
  /** `null` = both price lists (the ?agreement_type= param is omitted). */
  agreementType: SplaAgreementType | null;
  /** `null` = archived included (the management-grid default); '0' / '1' narrow server-side. */
  archived: '0' | '1' | null;
}

interface UseAdminSplaLicensesReturn {
  licenses: SplaLicense[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /** Replace one license row in place (matched by `id`) from a PATCH/retire response. */
  applyLicense: (updated: SplaLicense) => void;
}

// The catalog in distributor-sheet order (category section → price list → row order — never
// re-sorted client-side). Filters are server-side query params; the text search stays client-side
// in the tab. `refreshKey` bumps force a refetch (cross-tab invalidation).
export const useAdminSplaLicenses = (
  enabled: boolean,
  filters: AdminSplaLicenseFilters,
  refreshKey = 0,
): UseAdminSplaLicensesReturn => {
  const [licenses, setLicenses] = useState<SplaLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const { agreementType, archived } = filters;

  const fetchLicenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (agreementType !== null) {
        params.set('agreement_type', agreementType);
      }
      if (archived !== null) {
        params.set('archived', archived);
      }
      const query = params.toString();
      const response = await apiFetch(`/admin/spla/licenses${query ? `?${query}` : ''}`);
      const body = (await response.json().catch(() => null)) as ApiResponseSplaLicenseList | null;
      if (body?.status === 'success' && body.data?.licenses) {
        setLicenses(body.data.licenses);
      } else {
        setError(body?.message ?? 'Failed to load the SPLA license catalog');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the SPLA catalog');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, agreementType, archived]);

  const applyLicense = useCallback((updated: SplaLicense) => {
    setLicenses((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchLicenses();
    }
    // refreshKey re-runs the fetch when a sibling tab invalidates the catalog.
  }, [enabled, auth.isAuthenticated, fetchLicenses, refreshKey]);

  return { licenses, loading, error, refetch: fetchLicenses, applyLicense };
};

interface UseAdminSplaCategoriesReturn {
  categories: SplaCategory[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// All categories in sheet order, with `license_count` (drives the delete affordance). Consumed by
// the categories modal and the license create/edit modal's category select — both pass their
// `isOpen` as `enabled` so the list is fetched fresh per open.
export const useAdminSplaCategories = (enabled: boolean): UseAdminSplaCategoriesReturn => {
  const [categories, setCategories] = useState<SplaCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/admin/spla/categories');
      const body = (await response.json().catch(() => null)) as ApiResponseSplaCategoryList | null;
      if (body?.status === 'success' && body.data?.categories) {
        setCategories(body.data.categories);
      } else {
        setError(body?.message ?? 'Failed to load the SPLA categories');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the SPLA categories');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchCategories();
    }
  }, [enabled, auth.isAuthenticated, fetchCategories]);

  return { categories, loading, error, refetch: fetchCategories };
};

interface UseAdminSplaLicensePricingReturn {
  pricing: SplaLicensePricing | null;
  loading: boolean;
  error: string | null;
  /** Resolves with the fresh pricing (or null on failure) so callers can chain on it. */
  refetch: () => Promise<SplaLicensePricing | null>;
}

// One license's sale-price grid (platform default + per-client overrides + the distributor price
// for margin context). Mounted per-license by the pricing modal, which passes `isOpen` as `enabled`
// — the useProductPricing precedent.
export const useAdminSplaLicensePricing = (
  licenseId: number,
  enabled: boolean,
): UseAdminSplaLicensePricingReturn => {
  const [pricing, setPricing] = useState<SplaLicensePricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchPricing = useCallback(async (): Promise<SplaLicensePricing | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/spla/licenses/${licenseId}/price`);
      const body = (await response.json().catch(() => null)) as ApiResponseSplaLicensePricing | null;
      if (body?.status === 'success' && body.data?.price) {
        setPricing(body.data.price);
        return body.data.price;
      }
      setError(body?.message ?? 'Failed to load the license pricing');
      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the license pricing');
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiFetch, licenseId]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchPricing();
    }
  }, [enabled, auth.isAuthenticated, fetchPricing]);

  return { pricing, loading, error, refetch: fetchPricing };
};
